import { useState, useEffect, useCallback } from "react";
import type { Role } from "@/hooks/useAdminAuth";
import { ALL_PERMISSIONS } from "@/pages/admin/system/constants";

interface RolesPanelProps {
  fetchRoles: () => Promise<any>;
  createRole: (payload: Record<string, unknown>) => Promise<any>;
  updateRole: (roleId: number, payload: Record<string, unknown>) => Promise<any>;
  deleteRole: (roleId: number) => Promise<any>;
  onMessage: (msg: string) => void;
}

export default function RolesPanel({
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  onMessage,
}: RolesPanelProps) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({
    name: "", permissions: [] as string[], is_default: false,
    client_sub_permissions: {} as Record<string, boolean>,
    progress_sub_permissions: {} as Record<string, boolean>,
    finance_sub_permissions: {} as Record<string, boolean>,
    system_sub_permissions: {} as Record<string, boolean>,
    data_scope: "personal" as "global" | "personal",
  });
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<Role | null>(null);
  const [deleteRoleLoading, setDeleteRoleLoading] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  // Warning states: show red text when user tries to uncheck last sub-permission
  const [clientSubWarn, setClientSubWarn] = useState(false);
  const [financeSubWarn, setFinanceSubWarn] = useState(false);
  const [systemSubWarn, setSystemSubWarn] = useState(false);

  const loadRoles = useCallback(async () => {
    setRoleLoading(true);
    const res = await fetchRoles();
    setRoleLoading(false);
    if (res?.success) setRoles(res.roles || []);
  }, [fetchRoles]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const togglePermission = (key: string) => {
    setRoleForm((prev) => {
      const hasPerm = prev.permissions.includes(key);
      const newPermissions = hasPerm ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key];
      let newClientSub = prev.client_sub_permissions;
      let newSysSub = prev.system_sub_permissions;
      let newFinSub = prev.finance_sub_permissions;
      if (!hasPerm) {
        // Enabling a module: auto default-check first sub-perm if none selected
        if (key === "clients" && Object.values(prev.client_sub_permissions).every((v) => !v)) {
          newClientSub = { ...prev.client_sub_permissions, assign_advisor: true };
        }
        if (key === "system" && Object.values(prev.system_sub_permissions).every((v) => !v)) {
          newSysSub = { ...prev.system_sub_permissions, notification: true };
        }
        if (key === "finance" && Object.values(prev.finance_sub_permissions).every((v) => !v)) {
          newFinSub = { ...prev.finance_sub_permissions, stats: true };
        }
      } else {
        // Disabling module: clear sub-perms
        if (key === "clients") newClientSub = {};
        if (key === "system") newSysSub = {};
        if (key === "finance") newFinSub = {};
      }
      return { ...prev, permissions: newPermissions, client_sub_permissions: newClientSub, system_sub_permissions: newSysSub, finance_sub_permissions: newFinSub };
    });
  };

  const toggleSubPermission = (key: string) => {
    const selectedKeys = ["assign_advisor", "edit_client", "delete_client"].filter((k) => roleForm.client_sub_permissions[k]);
    const isSelected = roleForm.client_sub_permissions[key];
    if (isSelected && selectedKeys.length === 1) {
      setClientSubWarn(true);
      setTimeout(() => setClientSubWarn(false), 2500);
      return;
    }
    setClientSubWarn(false);
    setRoleForm((prev) => ({ ...prev, client_sub_permissions: { ...prev.client_sub_permissions, [key]: !prev.client_sub_permissions[key] } }));
  };

  const toggleProgressSubPermission = (key: string) => {
    setRoleForm((prev) => ({ ...prev, progress_sub_permissions: { ...prev.progress_sub_permissions, [key]: !prev.progress_sub_permissions[key] } }));
  };

  const toggleFinanceSubPermission = (key: string) => {
    const selectedKeys = ["stats", "income", "expense"].filter((k) => roleForm.finance_sub_permissions[k]);
    const isSelected = roleForm.finance_sub_permissions[key];
    if (isSelected && selectedKeys.length === 1) {
      setFinanceSubWarn(true);
      setTimeout(() => setFinanceSubWarn(false), 2500);
      return;
    }
    setFinanceSubWarn(false);
    setRoleForm((prev) => ({ ...prev, finance_sub_permissions: { ...prev.finance_sub_permissions, [key]: !prev.finance_sub_permissions[key] } }));
  };

  const toggleSystemSubPermission = (key: string) => {
    const selectedKeys = ["notification", "roles", "employees", "logs"].filter((k) => roleForm.system_sub_permissions[k]);
    const isSelected = roleForm.system_sub_permissions[key];
    if (isSelected && selectedKeys.length === 1) {
      setSystemSubWarn(true);
      setTimeout(() => setSystemSubWarn(false), 2500);
      return;
    }
    setSystemSubWarn(false);
    setRoleForm((prev) => ({ ...prev, system_sub_permissions: { ...prev.system_sub_permissions, [key]: !prev.system_sub_permissions[key] } }));
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) { onMessage("角色名称不能为空"); return; }
    setRoleLoading(true);
    const rolePayload = { name: roleForm.name.trim(), permissions: roleForm.permissions, is_default: roleForm.is_default, client_sub_permissions: roleForm.client_sub_permissions, progress_sub_permissions: roleForm.progress_sub_permissions, finance_sub_permissions: roleForm.finance_sub_permissions, system_sub_permissions: roleForm.system_sub_permissions, data_scope: roleForm.data_scope };
    const res = editingRole ? await updateRole(editingRole.id, rolePayload) : await createRole(rolePayload);
    setRoleLoading(false);
    if (res?.success) {
      setShowRoleModal(false); setEditingRole(null); loadRoles();
      onMessage(editingRole ? "角色更新成功" : "角色创建成功");
    } else onMessage(res?.error || "操作失败");
  };

  const handleDeleteRole = async () => {
    if (!deleteRoleTarget) return;
    setDeleteRoleLoading(true);
    const res = await deleteRole(deleteRoleTarget.id);
    setDeleteRoleLoading(false);
    if (res?.success) { setDeleteRoleTarget(null); loadRoles(); onMessage("删除成功"); }
    else { setDeleteRoleTarget(null); onMessage(res?.error || "删除失败"); }
  };

  const openRoleModal = (role?: Role) => {
    setClientSubWarn(false);
    setFinanceSubWarn(false);
    setSystemSubWarn(false);
    if (role) {
      setEditingRole(role);
      setRoleForm({ name: role.name, permissions: role.permissions || [], is_default: role.is_default, client_sub_permissions: role.client_sub_permissions || {}, progress_sub_permissions: role.progress_sub_permissions || {}, finance_sub_permissions: role.finance_sub_permissions || {}, system_sub_permissions: role.system_sub_permissions || {}, data_scope: role.data_scope || "personal" });
    } else {
      setEditingRole(null);
      setRoleForm({ name: "", permissions: [], is_default: false, client_sub_permissions: {}, progress_sub_permissions: {}, finance_sub_permissions: {}, system_sub_permissions: {}, data_scope: "personal" });
    }
    setShowRoleModal(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1a3a5c]">角色列表</h3>
          <p className="text-[11px] text-gray-400 mt-0.5">为不同角色配置后台功能模块权限</p>
        </div>
        <button onClick={() => openRoleModal()} className="px-3 py-2 bg-[#1a3a5c] text-white rounded-lg text-xs font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
          <i className="ri-add-line"></i>新增角色
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {roleLoading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : roles.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm"><i className="ri-inbox-line text-2xl mb-2 block"></i>暂无角色数据</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {roles.map((role) => (
              <div key={role.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-[#1a3a5c]">{role.name}</span>
                    {role.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#c9a96e]/10 text-[#c9a96e]">默认角色</span>}
                    {role.data_scope === "global" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1a3a5c]/10 text-[#1a3a5c]">全局视图</span>}
                    {role.data_scope === "personal" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">个人视图</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(role.permissions || []).length === 0 ? (
                      <span className="text-[11px] text-gray-300">暂无权限</span>
                    ) : (role.permissions || []).map((p) => {
                      const permLabel = ALL_PERMISSIONS.find((ap) => ap.key === p)?.label || p;

                      // Clients sub-permissions display
                      const clientSubMap: Record<string, { label: string; icon: string }> = {
                        assign_advisor: { label: "分配顾问", icon: "ri-user-star-line" },
                        edit_client: { label: "编辑", icon: "ri-edit-line" },
                        delete_client: { label: "删除", icon: "ri-delete-bin-line" },
                      };
                      if (p === "clients" && role.client_sub_permissions) {
                        const selected = Object.entries(role.client_sub_permissions)
                          .filter(([k, v]) => v && clientSubMap[k])
                          .map(([k]) => clientSubMap[k]);
                        if (selected.length > 0 && selected.length < 3) {
                          return (
                            <span key={p} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#1a3a5c]/5 text-[#1a3a5c] border border-[#1a3a5c]/10">
                              <i className="ri-user-line text-[10px]"></i>
                              {permLabel}（{selected.map((s, idx) => (
                                <span key={idx} className="inline-flex items-center gap-0.5">
                                  {idx > 0 && <span className="text-[#1a3a5c]/40">、</span>}
                                  <i className={`${s.icon} text-[10px]`}></i>{s.label}
                                </span>
                              ))}）
                            </span>
                          );
                        }
                      }

                      // Finance sub-permissions display
                      const financeSubMap: Record<string, { label: string; icon: string }> = {
                        stats: { label: "统计", icon: "ri-bar-chart-line" },
                        income: { label: "收入", icon: "ri-arrow-down-circle-line" },
                        expense: { label: "支出", icon: "ri-arrow-up-circle-line" },
                      };
                      if (p === "finance" && role.finance_sub_permissions) {
                        const selected = Object.entries(role.finance_sub_permissions)
                          .filter(([k, v]) => v && financeSubMap[k])
                          .map(([k]) => financeSubMap[k]);
                        if (selected.length > 0 && selected.length < 3) {
                          return (
                            <span key={p} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                              <i className="ri-funds-line text-[10px]"></i>
                              {permLabel}（{selected.map((s, idx) => (
                                <span key={idx} className="inline-flex items-center gap-0.5">
                                  {idx > 0 && <span className="text-emerald-400">、</span>}
                                  <i className={`${s.icon} text-[10px]`}></i>{s.label}
                                </span>
                              ))}）
                            </span>
                          );
                        }
                      }

                      // System sub-permissions display
                      const systemSubMap: Record<string, { label: string; icon: string }> = {
                        notification: { label: "通知管理", icon: "ri-notification-3-line" },
                        roles: { label: "角色管理", icon: "ri-shield-keyhole-line" },
                        employees: { label: "员工管理", icon: "ri-team-line" },
                        logs: { label: "操作日志", icon: "ri-history-line" },
                      };
                      if (p === "system" && role.system_sub_permissions) {
                        const selected = Object.entries(role.system_sub_permissions)
                          .filter(([k, v]) => v && systemSubMap[k])
                          .map(([k]) => systemSubMap[k]);
                        if (selected.length > 0 && selected.length < 4) {
                          return (
                            <span key={p} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                              <i className="ri-settings-3-line text-[10px]"></i>
                              {permLabel}（{selected.map((s, idx) => (
                                <span key={idx} className="inline-flex items-center gap-0.5">
                                  {idx > 0 && <span className="text-indigo-400">、</span>}
                                  <i className={`${s.icon} text-[10px]`}></i>{s.label}
                                </span>
                              ))}）
                            </span>
                          );
                        }
                      }

                      return <span key={p} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{permLabel}</span>;
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openRoleModal(role)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-[#1a3a5c]/10 flex items-center justify-center text-gray-400 hover:text-[#1a3a5c] transition-colors cursor-pointer"><i className="ri-edit-line text-xs"></i></button>
                  <button onClick={() => setDeleteRoleTarget(role)} className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-xs"></i></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-base font-bold text-[#1a3a5c]">{editingRole ? "编辑角色" : "新增角色"}</h3>
              <button onClick={() => setShowRoleModal(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">角色名称 <span className="text-red-400">*</span></label>
                <input type="text" value={roleForm.name} onChange={(e) => setRoleForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white" placeholder="请输入角色名称" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">功能模块权限</label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map((p) => (
                    <button key={p.key} onClick={() => togglePermission(p.key)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors cursor-pointer text-left ${roleForm.permissions.includes(p.key) ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}>
                      <i className={roleForm.permissions.includes(p.key) ? "ri-checkbox-circle-line" : "ri-checkbox-blank-circle-line"}></i>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {roleForm.permissions.includes("clients") && (
                <div className="bg-[#1a3a5c]/5 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-[#1a3a5c] flex items-center gap-1.5"><i className="ri-shield-user-line"></i>客户管理二级权限</p>
                  {clientSubWarn && (
                    <p className="text-[10px] text-red-500 font-medium animate-pulse">需至少保留 1 项二级权限</p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "assign_advisor", label: "分配顾问", icon: "ri-user-star-line" },
                      { key: "edit_client", label: "编辑客户", icon: "ri-edit-line" },
                      { key: "delete_client", label: "删除客户", icon: "ri-delete-bin-line" },
                    ].map((sub) => {
                      const isSelected = roleForm.client_sub_permissions[sub.key];
                      const selectedClientKeys = ["assign_advisor", "edit_client", "delete_client"].filter((k) => roleForm.client_sub_permissions[k]);
                      const isLastSelected = isSelected && selectedClientKeys.length === 1;
                      return (
                        <button key={sub.key} onClick={() => {
                          if (isLastSelected) { setClientSubWarn(true); setTimeout(() => setClientSubWarn(false), 2500); return; }
                          toggleSubPermission(sub.key);
                        }}
                          title={isLastSelected ? "需至少保留 1 项二级权限" : undefined}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left ${isSelected ? "bg-[#1a3a5c] text-white cursor-pointer" : "bg-white text-gray-500 hover:bg-gray-50 cursor-pointer"}`}>
                          <i className={sub.icon}></i>
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {roleForm.permissions.includes("progress") && (
                <div className="bg-[#c9a96e]/10 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-[#c9a96e] flex items-center gap-1.5"><i className="ri-money-cny-circle-line"></i>进度管理二级权限</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ key: "expense_management", label: "支出管理" }].map((sub) => (
                      <button key={sub.key} onClick={() => toggleProgressSubPermission(sub.key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer text-left ${roleForm.progress_sub_permissions[sub.key] ? "bg-[#c9a96e] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                        <i className={roleForm.progress_sub_permissions[sub.key] ? "ri-checkbox-circle-line" : "ri-checkbox-blank-circle-line"}></i>
                        {sub.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {roleForm.permissions.includes("finance") && (
                <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5"><i className="ri-funds-line"></i>财务管理二级权限</p>
                  {financeSubWarn && (
                    <p className="text-[10px] text-red-500 font-medium animate-pulse">需至少保留 1 项二级权限</p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "stats", label: "统计", icon: "ri-bar-chart-line" },
                      { key: "income", label: "收入", icon: "ri-arrow-down-circle-line" },
                      { key: "expense", label: "支出", icon: "ri-arrow-up-circle-line" },
                    ].map((sub) => {
                      const isSelected = roleForm.finance_sub_permissions[sub.key];
                      const selectedFinKeys = ["stats", "income", "expense"].filter((k) => roleForm.finance_sub_permissions[k]);
                      const isLastSelected = isSelected && selectedFinKeys.length === 1;
                      return (
                        <button key={sub.key} onClick={() => {
                          if (isLastSelected) { setFinanceSubWarn(true); setTimeout(() => setFinanceSubWarn(false), 2500); return; }
                          toggleFinanceSubPermission(sub.key);
                        }}
                          title={isLastSelected ? "需至少保留 1 项二级权限" : undefined}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left ${isSelected ? "bg-emerald-600 text-white cursor-pointer" : "bg-white text-gray-500 hover:bg-gray-50 cursor-pointer"}`}>
                          <i className={sub.icon}></i>
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {roleForm.permissions.includes("system") && (
                <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5"><i className="ri-settings-3-line"></i>系统管理二级权限</p>
                  {systemSubWarn && (
                    <p className="text-[10px] text-red-500 font-medium animate-pulse">需至少保留 1 项二级权限</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "notification", label: "通知管理", icon: "ri-notification-3-line" },
                      { key: "roles", label: "角色管理", icon: "ri-shield-keyhole-line" },
                      { key: "employees", label: "员工管理", icon: "ri-team-line" },
                      { key: "logs", label: "操作日志", icon: "ri-history-line" },
                    ].map((sub) => {
                      const isSelected = roleForm.system_sub_permissions[sub.key];
                      const selectedSysKeys = ["notification", "roles", "employees", "logs"].filter((k) => roleForm.system_sub_permissions[k]);
                      const isLastSelected = isSelected && selectedSysKeys.length === 1;
                      return (
                        <button key={sub.key} onClick={() => {
                          if (isLastSelected) { setSystemSubWarn(true); setTimeout(() => setSystemSubWarn(false), 2500); return; }
                          toggleSystemSubPermission(sub.key);
                        }}
                          title={isLastSelected ? "需至少保留 1 项二级权限" : undefined}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left ${isSelected ? "bg-indigo-600 text-white cursor-pointer" : "bg-white text-gray-500 hover:bg-gray-50 cursor-pointer"}`}>
                          <i className={sub.icon}></i>
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-[#1a3a5c] flex items-center gap-1.5"><i className="ri-database-2-line"></i>数据视图范围</p>
                <div className="flex gap-2">
                  {[
                    { scope: "global", title: "全局视图", desc: "可查看所有客户数据" },
                    { scope: "personal", title: "个人视图", desc: "仅查看个人名下客户" },
                  ].map((opt) => (
                    <button key={opt.scope} onClick={() => setRoleForm((f) => ({ ...f, data_scope: opt.scope as "global" | "personal" }))}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors cursor-pointer text-left ${roleForm.data_scope === opt.scope ? "bg-[#1a3a5c] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                      <i className={roleForm.data_scope === opt.scope ? "ri-checkbox-circle-line" : "ri-checkbox-blank-circle-line"}></i>
                      <div>
                        <p className="font-medium">{opt.title}</p>
                        <p className="text-[10px] opacity-80 mt-0.5">{opt.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={roleForm.is_default} onChange={(e) => setRoleForm((f) => ({ ...f, is_default: e.target.checked }))} className="rounded border-gray-300" />
                <span className="text-xs text-gray-600">设为默认角色</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowRoleModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSaveRole} disabled={roleLoading} className="px-5 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {roleLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>保存中...</> : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRoleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-[#1a3a5c] mb-1">确认删除角色？</h3>
            <p className="text-xs text-gray-400 mb-5">
              此操作将永久删除角色「<span className="font-medium text-[#1a3a5c]">{deleteRoleTarget.name}</span>」，
              请先移除该角色的员工。
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteRoleTarget(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleDeleteRole} disabled={deleteRoleLoading} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                {deleteRoleLoading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>删除中...</> : "确认删除"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}